import type { AdminUserSummary, KycCase } from "../types";

/**
 * The queue's state column carries two vocabularies at once. A profile matched to a
 * banking customer reports the Cybrid *customer* state — `storing`, `unverified`,
 * `verified`, `rejected`, `frozen` — while a Firestore-only profile reports the app's
 * derived *KYC status*: `waiting`, `reviewing`, `completed`, `expired`, `rejected`,
 * `frozen`. Bucketing the raw string left `storing`, `unverified`, `waiting`,
 * `reviewing` and `frozen` all unclassified, so the working population of the queue
 * landed in a single meaningless "other" pile.
 *
 * Classify by the move the state implies instead. Every stage below answers "who is
 * this waiting on", which is the only question that changes what an operator does.
 */
export type KycStage = "verified" | "processing" | "customerAction" | "blocked" | "unknown";

export const KYC_STAGES: readonly KycStage[] = ["blocked", "customerAction", "processing", "verified", "unknown"];

const STAGE_BY_STATE: Readonly<Record<string, KycStage>> = {
  verified: "verified",
  completed: "verified",
  approved: "verified",

  // The provider holds these; nothing an operator does moves them along.
  storing: "processing",
  reviewing: "processing",
  review: "processing",
  manual_review: "processing",
  in_progress: "processing",
  started: "processing",
  created: "processing",
  pending: "processing",

  // The customer holds these. `failed` belongs here rather than with the declines:
  // the app derives a failed identity verification to `expired`, meaning restart.
  waiting: "customerAction",
  unverified: "customerAction",
  expired: "customerAction",
  failed: "customerAction",
  not_started: "customerAction",

  // Provider decisions. Neither this portal nor the banking API can overturn them.
  rejected: "blocked",
  frozen: "blocked"
};

export function kycStage(state: string): KycStage {
  return STAGE_BY_STATE[state.trim().toLowerCase()] ?? "unknown";
}

export interface KycStageInfo {
  label: string;
  tone: "good" | "neutral" | "warn" | "danger";
  waitingOn: string;
  summary: string;
  /** What this portal can actually do — stated in terms of the levers that exist. */
  action: string;
}

const STAGE_INFO: Readonly<Record<KycStage, KycStageInfo>> = {
  verified: {
    label: "Verified",
    tone: "good",
    waitingOn: "Nobody",
    summary: "The provider reports this customer as verified and the app has the approval.",
    action: "Nothing to do. Recheck only if the customer reports being blocked in the app."
  },
  processing: {
    label: "Provider processing",
    tone: "neutral",
    waitingOn: "Provider",
    summary: "The provider is still working the verification. `storing` and `reviewing` both sit here.",
    action: "Wait. Recheck pulls the provider's current answer but does not push it along; a case stuck here for days is a poller or provider problem, not a customer one."
  },
  customerAction: {
    label: "Waiting on customer",
    tone: "warn",
    waitingOn: "Customer",
    summary: "Verification was never finished, or the session lapsed. The provider is waiting on the customer.",
    action: "Send the verification-action notice so the customer can restart in the app, then move the case to waiting for customer."
  },
  blocked: {
    label: "Declined or frozen",
    tone: "danger",
    waitingOn: "Compliance",
    summary: "The provider declined or froze this customer. The banking API returns no adjudication reason, and no admin action here can reverse the decision.",
    action: "Escalate to the provider through their compliance queue, then track the ticket here. Their SLA is two calendar days. Do not read system failure codes as a rejection reason, and do not offer the customer a retry — the provider refuses a new verification for a declined or frozen customer."
  },
  unknown: {
    label: "Unmapped state",
    tone: "neutral",
    waitingOn: "Unclear",
    summary: "This state does not map to a known verification decision.",
    action: "Recheck to refresh the provider's answer, and escalate if it stays unmapped — an unmapped state usually means the provider added one."
  }
};

export function kycStageInfo(stage: KycStage): KycStageInfo {
  return STAGE_INFO[stage];
}

export function kycGuidance(state: string): KycStageInfo {
  return kycStageInfo(kycStage(state));
}

/** A case is only open while the provider has not verified and nobody has resolved it. */
export function isKycCaseOpen(user: AdminUserSummary, item?: KycCase): boolean {
  return kycStage(user.state) !== "verified" && item?.workflowStatus !== "resolved";
}

export function kycAgeDays(user: AdminUserSummary, item?: KycCase, now = Date.now()): number | null {
  const timestamp = item?.updatedAt || user.identityCreatedAt || user.createdAt;
  if (!timestamp) return null;
  const parsed = Date.parse(timestamp);
  if (!Number.isFinite(parsed)) return null;
  return Math.max(0, Math.floor((now - parsed) / 86_400_000));
}

export function isKycSlaRisk(user: AdminUserSummary, item?: KycCase, now = Date.now()): boolean {
  if (!isKycCaseOpen(user, item)) return false;
  const age = kycAgeDays(user, item, now);
  return age !== null && age >= 2;
}
